'use client';

import { useCallback, useId, useMemo, useRef, useState } from 'react';

import classnames from 'classnames';

import { Button } from '../../button';
import { Popover, PopoverContent, PopoverTrigger } from '../../popover/Popover';
import { FieldDropdownIcon } from '../layout/FieldDropdownIcon';
import { MultiselectOptions } from '../layout/MultiselectOptions';
import { getButtonVariant } from '../layout/SelectOption';
import type { SelectOption } from '../layout/SelectOption.types';
import { SelectOptions } from '../layout/SelectOptions';
import type { MultiselectDropdownProps } from './MultiselectDropdown.types';

import styles from './MultiselectDropdown.module.scss';

export function MultiselectDropdown({
  style,
  className,
  onSelect,
  options,
  optionLabels,
  disabled = false,
  error = false,
  placeholder = 'Select...',
  testId = 'ui-multiselect-dropdown',
}: MultiselectDropdownProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  const dropdownId = useId();
  const listboxId = `${dropdownId}-listbox`;
  const buttonId = `${dropdownId}-button`;

  const values = useMemo(
    () =>
      options
        .filter((option) => option.selected)
        .map(({ value, label, customLabel }) => ({
          value,
          label,
          customLabel,
        })),
    [options],
  );

  const availableOptions = useMemo(
    () =>
      options.filter(
        (option) =>
          !option.disabled && !values.some((v) => v.value === option.value),
      ),
    [options, values],
  );

  const dispatchChangeEvent = useCallback(() => {
    containerRef.current?.dispatchEvent(new Event('change', { bubbles: true }));
  }, []);

  const handleOptionSelect = useCallback(
    (option: SelectOption) => {
      if (option.disabled || values.some((v) => v.value === option.value)) {
        return;
      }

      const newValues = [
        ...values,
        {
          value: option.value,
          label: option.label,
          customLabel: option.customLabel,
        },
      ];
      onSelect(newValues.map((v) => v.value));
      dispatchChangeEvent();
      setIsOpen(false);
      buttonRef.current?.focus();
    },
    [values, onSelect, dispatchChangeEvent],
  );

  const handleRemoveSelectedValue = useCallback(
    (value: string | number) => {
      const newValues = values.filter((v) => v.value !== value);
      onSelect(newValues.map((v) => v.value));
      dispatchChangeEvent();
    },
    [values, onSelect, dispatchChangeEvent],
  );

  const handleButtonClick = useCallback(() => {
    if (!disabled) {
      setIsOpen((prev) => !prev);
    }
  }, [disabled]);

  const handleDropdownIconClick = useCallback(() => {
    if (!disabled) {
      setIsOpen((prev) => {
        if (!prev) buttonRef.current?.focus();
        return !prev;
      });
    }
  }, [disabled]);

  return (
    <Popover
      open={isOpen}
      onOpenChange={setIsOpen}
      placement="bottom-start"
      strategy="fixed"
      matchReferenceWidth
      dismissOnAncestorScroll
    >
      <div
        ref={containerRef}
        style={style}
        className={classnames(styles['multiselect-dropdown'], className)}
        data-testid={testId}
      >
        <PopoverTrigger asChild>
          <Button
            ref={buttonRef}
            id={buttonId}
            className={styles['button']}
            variant={getButtonVariant(error)}
            tabIndex={disabled ? -1 : 0}
            onClick={handleButtonClick}
            disabled={disabled}
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            aria-controls={listboxId}
            aria-label={placeholder}
            aria-multiselectable="true"
          >
            <span className={styles['label']}>{placeholder}</span>
          </Button>
        </PopoverTrigger>
        <FieldDropdownIcon
          disabled={disabled}
          error={error}
          onClick={handleDropdownIconClick}
        />
      </div>

      {values.length > 0 && (
        <div
          className={styles['selected-items']}
          role="list"
          aria-label="Selected items"
        >
          <MultiselectOptions
            testId={`${testId}_selected`}
            options={values}
            onClick={(option: SelectOption) =>
              handleRemoveSelectedValue(option.value)
            }
            disabled={disabled}
          />
        </div>
      )}
      {isOpen && availableOptions.length > 0 && (
        <PopoverContent style={{ padding: 0 }}>
          <SelectOptions
            testId={`${testId}_options`}
            options={availableOptions}
            onClick={handleOptionSelect}
            labels={optionLabels}
            id={listboxId}
            role="listbox"
            aria-labelledby={buttonId}
          />
        </PopoverContent>
      )}
    </Popover>
  );
}
